<template>
    <page-container data-test="external-lists-list">
        <page-header class="mt-8"> External Lists </page-header>

        <external-list-list-filters
            v-if="totalCount > 0 || !isEqual(defaultFilters(), fetchParams)"
            v-model="fetchParams"
            @reset="resetFilters"
            @apply="applyFilters"
        ></external-list-list-filters>

        <div v-if="loading && !list.length" class="text-center py-10">
            <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
        </div>

        <div v-if="!loading && !list.length" data-test="external-lists-list-message">
            <empty-state v-if="totalCount === 0 && !isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else-if="totalCount === 0">No external lists yet.</empty-state>
        </div>

        <vx-table v-if="list.length" rowBreakpoint="md" :style="gridStyle">
            <vx-table-row isHeader>
                <div class="hidden lg:block">Name</div>
                <div>Contacts</div>
                <div v-if="FEATURES.contact_groups">Groups</div>
                <div>Vendor</div>
                <div>Status</div>
                <div>Created at</div>
            </vx-table-row>

            <external-list-row
                v-for="(externalList, index) in list"
                :external-list="list[index]"
                :key="list[index].id"
            ></external-list-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                class="grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">{{ list.length }}/{{ totalCount }}</div>
        </div>
    </page-container>
</template>
<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ExternalListListFilters from './ExternalListListFilters.vue';
import ExternalListRow from './ExternalListRow.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { externalListsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { isEqual } from '~/utils/helpers';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useExternalListsContactCount } from '~/composables/useExternalListsContactCount';
import { useExternalListsGroupsCount } from '~/composables/useExternalListsGroupsCount';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const teamId = Spark.state.currentTeam.id;
const FEATURES = inject('FEATURES');

const toasted = useToasted();

const { currentGroup, currentOrgUnit } = useCurrentGroup();

const context = computed(() => ({
    teamId,
    orgUnitId: currentOrgUnit.value?.id,
    groupId: currentGroup.value?.group_id,
}));

const gridStyle = computed(() => {
    if (FEATURES.contact_groups) {
        return {
            display: 'grid',
            gridTemplateColumns: '1fr 120px 100px 100px 100px 150px auto',
            columnGap: '1.5rem',
            rowGap: '0.5rem',
        };
    } else {
        return {
            display: 'grid',
            gridTemplateColumns: '1fr 120px 100px 100px 150px auto',
            columnGap: '1.5rem',
            rowGap: '0.5rem',
        };
    }
});

const { fetchExternalListsContactCount } = useExternalListsContactCount();
const { fetchExternalListsGroupsCount, groupCounts } = useExternalListsGroupsCount();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => {
    return {
        query: '',
        per_page: 10,
        cursor: null,
        sort_field: 'id',
        sort_dir: 'desc',
    };
};

const totalCount = ref(0);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const fetchTotalCount = async () => {
    try {
        const response = await externalListsClient.getExternalListsCount(context.value, { ...fetchParams.value });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const loadData = () => {
    fetchRecords(externalListsClient.getExternalLists(context.value, { ...fetchParams.value })).then((data) => {
        if (data?.length) {
            fetchExternalListsContactCount(context.value, {
                external_contact_list_ids: data.map((externalList) => externalList.id),
            });

            if (FEATURES.contact_groups) {
                const connectedListIds = [];
                data.forEach((externalList) => {
                    if (externalList.status === 'connected') {
                        connectedListIds.push(externalList.id);
                    } else {
                        groupCounts.value[externalList.id] = null;
                    }
                });
                fetchExternalListsGroupsCount(connectedListIds);
            }
        }
    });
    fetchTotalCount();
};
</script>
